import type { MaybeRefOrGetter } from '@/composition/vue/compositionApi'
import { computed, toValue } from '@/composition/vue/compositionApi'
import {
  isArchiveMime,
  isAudioMime,
  isCodeMime,
  isContactMime,
  isDocumentMime,
  isEmailMessageMime,
  isImageMime,
  isJsonMime,
  isPdfMime,
  isPresentationMime,
  isSpreadsheetMime,
  isTextMime,
  isVectorMime,
  isVideoMime,
} from '@/utils/file/mime'

const images = import.meta.glob('@/assets/images/attachments/*.svg', { eager: true })

export type FileTypeIcons = {
  fileArchive: string
  fileAudio: string
  fileContact: string
  fileDoc: string
  fileHtml: string
  fileJson: string
  fileMail: string
  filePdf: string
  filePpt: string
  fileSvg: string
  fileText: string
  fileVideo: string
  fileXls: string
  fileImage: string
  fileUnknown: string
}

export const fileTypeIcons = Object.entries(images).reduce((result, [key, value]) => {
  const fileName = key.split('/').at(-1)!.replace('.svg', '')
  result[fileName] = (value as { default: string }).default
  return result
}, {} as FileTypeIcons)

export const useFileDescription = (mimeTypeParam: MaybeRefOrGetter<string>) => {
  const getMime = () => toValue(mimeTypeParam)

  const isDoc = computed(() => isDocumentMime(getMime()))
  const isSpreadsheet = computed(() => isSpreadsheetMime(getMime()))
  const isPresentation = computed(() => isPresentationMime(getMime()))
  const isVideo = computed(() => isVideoMime(getMime()))
  const isVector = computed(() => isVectorMime(getMime()))
  const isAudio = computed(() => isAudioMime(getMime()))
  const isJson = computed(() => isJsonMime(getMime()))
  const isCode = computed(() => isCodeMime(getMime()))
  const isMail = computed(() => isEmailMessageMime(getMime()))
  const isPdf = computed(() => isPdfMime(getMime()))
  const isText = computed(() => isTextMime(getMime()))
  const isArchive = computed(() => isArchiveMime(getMime()))
  const isContact = computed(() => isContactMime(getMime()))
  // for vector images we have other icon
  const isImage = computed(() => isImageMime(getMime()) && !getMime().includes('svg'))

  const mimeTypeIcon = computed(() => {
    switch (true) {
      case isAudio.value:
        return fileTypeIcons.fileAudio
      case isDoc.value:
        return fileTypeIcons.fileDoc
      case isJson.value:
        return fileTypeIcons.fileJson
      case isCode.value:
        return fileTypeIcons.fileHtml
      case isMail.value:
        return fileTypeIcons.fileMail
      case isPdf.value:
        return fileTypeIcons.filePdf
      case isImage.value:
        return fileTypeIcons.fileImage
      case isPresentation.value:
        return fileTypeIcons.filePpt
      case isText.value:
        return fileTypeIcons.fileText
      case isVideo.value:
        return fileTypeIcons.fileVideo
      case isSpreadsheet.value:
        return fileTypeIcons.fileXls
      case isArchive.value:
        return fileTypeIcons.fileArchive
      case isVector.value:
        return fileTypeIcons.fileSvg
      case isContact.value:
        return fileTypeIcons.fileContact

      default:
        return fileTypeIcons.fileUnknown
    }
  })

  return {
    mimeTypeIcon,

    isAudio,
    isDoc,
    isJson,
    isCode,
    isMail,
    isPdf,
    isImage,
    isPresentation,
    isText,
    isVideo,
    isSpreadsheet,
    isArchive,
    isVector,
    isContact,
  }
}
